import React, { useState, useEffect, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import { Save, Eye, Upload, X, Image } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPageCMS = () => {
  const { isDark } = useTheme();
  const [content, setContent] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_description: '',
    hero_image_url: '',
    section_title: '',
    feature_1_title: '',
    feature_1_description: '',
    feature_1_icon: 'shield',
    feature_2_title: '',
    feature_2_description: '',
    feature_2_icon: 'users',
    feature_3_title: '',
    feature_3_description: '',
    feature_3_icon: 'calendar',
    feature_4_title: '',
    feature_4_description: '',
    feature_4_icon: 'trending-up',
    cta_title: '',
    cta_description: ''
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/landing-content`);
      if (response.ok) {
        const data = await response.json();
        setContent({
          hero_title: data.hero_title,
          hero_subtitle: data.hero_subtitle,
          hero_description: data.hero_description,
          hero_image_url: data.hero_image_url,
          section_title: data.section_title,
          feature_1_title: data.feature_1_title,
          feature_1_description: data.feature_1_description,
          feature_1_icon: data.feature_1_icon,
          feature_2_title: data.feature_2_title,
          feature_2_description: data.feature_2_description,
          feature_2_icon: data.feature_2_icon,
          feature_3_title: data.feature_3_title,
          feature_3_description: data.feature_3_description,
          feature_3_icon: data.feature_3_icon,
          feature_4_title: data.feature_4_title,
          feature_4_description: data.feature_4_description,
          feature_4_icon: data.feature_4_icon,
          cta_title: data.cta_title,
          cta_description: data.cta_description
        });
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);

    try {
      const response = await fetch(`${BACKEND_URL}/api/landing-content`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(content)
      });

      if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        alert('Failed to save changes');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    window.open('/', '_blank');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/uploads`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        // Use public URL for landing page images (no auth required)
        const publicUrl = data.url.replace('/api/uploads/', '/api/public/uploads/');
        setContent({ ...content, hero_image_url: publicUrl });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const clearHeroImage = () => {
    setContent({ ...content, hero_image_url: '' });
  };

  const inputClass = `w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm p-3`;
  const selectClass = `w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`;

  return (
    <AppLayout title="Landing Page Editor">
      <div className="max-w-4xl mx-auto">
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-8`}>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Edit Landing Page Content
              </h3>
              <p className="text-gray-400">Update the text content displayed on the public landing page</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handlePreview}
                data-testid="preview-button"
                className={`flex items-center gap-2 px-4 py-2 border ${isDark ? 'border-white/20 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} rounded-sm transition-colors`}
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-button"
                className={`flex items-center gap-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black px-6 py-2 font-bold rounded-sm transition-colors disabled:opacity-50`}
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
              </button>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Hero Title (First Line)
              </label>
              <input
                type="text"
                value={content.hero_title}
                onChange={(e) => setContent({ ...content, hero_title: e.target.value })}
                data-testid="hero-title-input"
                className={inputClass}
                placeholder="Elite B2B Events"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Hero Subtitle (Second Line - Highlighted)
              </label>
              <input
                type="text"
                value={content.hero_subtitle}
                onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })}
                data-testid="hero-subtitle-input"
                className={inputClass}
                placeholder="Outstanding Social Media"
              />
              <p className="text-xs text-gray-500 mt-1">This text will appear in lime green</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Hero Description
              </label>
              <textarea
                value={content.hero_description}
                onChange={(e) => setContent({ ...content, hero_description: e.target.value })}
                rows={4}
                data-testid="hero-description-input"
                className={inputClass}
                placeholder="Providing qualified B2B events..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Hero Image
              </label>
              
              {/* Image Preview */}
              {content.hero_image_url && (
                <div className="relative mb-3 inline-block">
                  <img 
                    src={content.hero_image_url?.startsWith('/') ? `${BACKEND_URL}${content.hero_image_url}` : content.hero_image_url} 
                    alt="Hero preview" 
                    className="max-w-xs h-32 object-cover rounded-sm border border-white/20"
                  />
                  <button
                    type="button"
                    onClick={clearHeroImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Upload Button and URL Input */}
              <div className="flex gap-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className={`flex items-center gap-2 px-4 py-3 ${isDark ? 'bg-[#111] border-white/20 hover:border-[#A2FF00]' : 'bg-gray-50 border-gray-300 hover:border-green-500'} border rounded-sm transition-colors disabled:opacity-50`}
                >
                  {uploadingImage ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[#A2FF00] border-t-transparent rounded-full animate-spin" />
                      <span className={isDark ? 'text-white' : 'text-gray-700'}>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                      <span className={isDark ? 'text-white' : 'text-gray-700'}>Upload Image</span>
                    </>
                  )}
                </button>
                <div className="flex-1 relative">
                  <input
                    type="url"
                    value={content.hero_image_url}
                    onChange={(e) => setContent({ ...content, hero_image_url: e.target.value })}
                    data-testid="hero-image-url-input"
                    className={inputClass}
                    placeholder="Or paste image URL..."
                  />
                  <Image className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Upload an image (max 5MB) or paste a direct URL from Unsplash, Pexels, etc.</p>
            </div>

            <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-6`}>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Features Section Title
              </label>
              <input
                type="text"
                value={content.section_title}
                onChange={(e) => setContent({ ...content, section_title: e.target.value })}
                data-testid="section-title-input"
                className={inputClass}
                placeholder="What We Deliver"
              />
              <p className="text-xs text-gray-500 mt-1">Last word will be highlighted in lime green</p>
            </div>

            {[1, 2, 3, 4].map((num) => (
              <div key={num} className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-6`}>
                <h4 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>Feature Box {num}</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Icon</label>
                    <select
                      value={content[`feature_${num}_icon`]}
                      onChange={(e) => setContent({ ...content, [`feature_${num}_icon`]: e.target.value })}
                      className={selectClass}
                    >
                      <option value="shield">Shield</option>
                      <option value="users">Users</option>
                      <option value="calendar">Calendar</option>
                      <option value="trending-up">Trending Up</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Title</label>
                    <input
                      type="text"
                      value={content[`feature_${num}_title`]}
                      onChange={(e) => setContent({ ...content, [`feature_${num}_title`]: e.target.value })}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Description</label>
                    <textarea
                      value={content[`feature_${num}_description`]}
                      onChange={(e) => setContent({ ...content, [`feature_${num}_description`]: e.target.value })}
                      rows={2}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            ))}

            <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-6`}>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Call-to-Action Title
              </label>
              <input
                type="text"
                value={content.cta_title}
                onChange={(e) => setContent({ ...content, cta_title: e.target.value })}
                data-testid="cta-title-input"
                className={inputClass}
                placeholder="Ready to Elevate Your Security Brand?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Call-to-Action Description
              </label>
              <textarea
                value={content.cta_description}
                onChange={(e) => setContent({ ...content, cta_description: e.target.value })}
                rows={3}
                data-testid="cta-description-input"
                className={inputClass}
                placeholder="Join the elite network..."
              />
            </div>
          </div>
        </div>

        <div className={`mt-6 ${isDark ? 'bg-[#0A0A0A] border-[#A2FF00]/20' : 'bg-green-50 border-green-200'} border rounded-sm p-6`}>
          <h4 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-700'} mb-2`}>Preview Note</h4>
          <p className="text-sm text-gray-400">
            Changes will be visible immediately on the landing page after saving. 
            Click "Preview" to see the current version in a new tab.
          </p>
        </div>
      </div>
    </AppLayout>
  );
};

export default LandingPageCMS;
